<div class="issue-detail-page">
  <div class="loading-center" *ngIf="loading()">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <div class="error-banner" *ngIf="error()">
    <mat-icon>error_outline</mat-icon>
    <span>{{ error() }}</span>
    <button mat-icon-button (click)="error.set(null)"><mat-icon>close</mat-icon></button>
  </div>

  <ng-container *ngIf="!loading() && issue() as iss">
    <app-breadcrumb [crumbs]="issueBreadcrumbs(iss)" />

    <div class="page-header issue-header">
      <div class="header-left">
        <button mat-icon-button (click)="goBack()"><mat-icon>arrow_back</mat-icon></button>
        <div>
          <div class="key-row">
            <span class="issue-key">{{ iss.key }}</span>
            <span class="tt-badge">{{ iss.typeLabel }}</span>
            <span class="tt-badge" [ngClass]="statusBadgeClass(iss.status)">{{ iss.statusLabel }}</span>
            <span class="tt-badge" [ngClass]="priorityBadgeClass(iss.priority)">{{ iss.priorityLabel }}</span>
            <span class="tt-badge" *ngIf="iss.epicKey">Epic: {{ iss.epicKey }}</span>
            <span class="tt-badge" *ngIf="iss.parentIssueKey">Parent: {{ iss.parentIssueKey }}</span>
            @if (iss.isOverdue) { <span class="tt-badge tt-badge--overdue">Overdue</span> }
          </div>
          <h1 class="summary-title">{{ iss.summary }}</h1>
          <p class="project-link">{{ iss.projectName }} · Reported by {{ iss.reporterName || 'Unknown' }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button mat-raised-button color="primary" [disabled]="saving()" (click)="save()">
          <mat-icon>save</mat-icon> {{ saving() ? 'Saving…' : 'Save' }}
        </button>
        <button mat-stroked-button (click)="cloneIssue()" [disabled]="saving()" matTooltip="Clone issue">
          <mat-icon>content_copy</mat-icon> Clone
        </button>
        <button mat-stroked-button (click)="openMergePanel()" [disabled]="saving()" matTooltip="Merge into another issue">
          <mat-icon>merge_type</mat-icon> Merge
        </button>
        <button mat-stroked-button *ngIf="iss.type === 3" (click)="toggleArchive()" [disabled]="saving()">
          <mat-icon>{{ iss.archivedAt ? 'unarchive' : 'archive' }}</mat-icon>
          {{ iss.archivedAt ? 'Restore' : 'Archive' }}
        </button>
        <button mat-stroked-button (click)="toggleWatch()" [matTooltip]="isWatching() ? 'Stop watching' : 'Watch issue'">
          <mat-icon>{{ isWatching() ? 'visibility' : 'visibility_off' }}</mat-icon>
          {{ isWatching() ? 'Watching' : 'Watch' }}
        </button>
        <button mat-stroked-button (click)="aiSuggestAc()" [disabled]="aiBusy()" matTooltip="Suggest acceptance criteria">
          <mat-icon>checklist</mat-icon> Suggest AC
        </button>
        <button mat-stroked-button (click)="aiPredictRisk()" [disabled]="aiBusy()" matTooltip="Predict risk">
          <mat-icon>warning</mat-icon> Predict risk
        </button>
        <button mat-stroked-button (click)="aiFindDuplicates()" [disabled]="aiBusy()" matTooltip="Find similar open issues">
          <mat-icon>find_in_page</mat-icon> Find duplicates
        </button>
      </div>
    </div>

    <div class="epic-progress" *ngIf="iss.epicProgress as ep">
      <div class="epic-progress-head">
        <strong>Epic progress</strong>
        <span>{{ ep.doneChildren }}/{{ ep.totalChildren }} done ({{ ep.percentComplete }}%) · {{ ep.storyPointsDone || 0 }}/{{ ep.storyPointsCommitted || 0 }} SP</span>
      </div>
      <mat-progress-bar mode="determinate" [value]="ep.percentComplete"></mat-progress-bar>
    </div>

    @if (showMergePanel) {
      <mat-card class="panel merge-panel">
        <mat-card-header><mat-card-title>Merge into another issue</mat-card-title></mat-card-header>
        <mat-card-content>
          <div class="form-row">
            <mat-form-field appearance="outline" class="grow">
              <mat-label>Target issue</mat-label>
              <mat-select [(ngModel)]="mergeTargetId">
                @for (opt of projectIssues(); track opt.id) {
                  @if (opt.id !== iss.id) {
                    <mat-option [value]="opt.id">{{ opt.key }} — {{ opt.summary }}</mat-option>
                  }
                }
              </mat-select>
            </mat-form-field>
            <mat-checkbox [(ngModel)]="mergeSoftDelete">Soft-delete source</mat-checkbox>
            <mat-checkbox [(ngModel)]="mergeCopyComments">Copy comments</mat-checkbox>
            <button mat-flat-button color="primary" type="button" [disabled]="saving() || !mergeTargetId" (click)="confirmMerge()">Merge</button>
            <button mat-button type="button" (click)="showMergePanel = false">Cancel</button>
          </div>
        </mat-card-content>
      </mat-card>
    }

    @if (duplicateHits().length) {
      <mat-card class="panel merge-panel">
        <mat-card-header><mat-card-title>Similar issues</mat-card-title></mat-card-header>
        <mat-card-content>
          <ul class="plain-list">
            @for (d of duplicateHits(); track d.key) {
              <li>
                <strong>{{ d.key }}</strong> ({{ (d.similarity * 100) | number:'1.0-0' }}%) — {{ d.summary }}
                <button mat-stroked-button type="button" *ngIf="d.id" [disabled]="saving()" (click)="mergeInto(d.id)">Merge into this</button>
              </li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
    }

    <mat-tab-group class="issue-tabs" animationDuration="150ms">
      <!-- Overview -->
      <mat-tab label="Overview">
        <div class="tab-body overview-grid">
          <div class="main-col">
            <mat-card class="panel">
              <mat-card-header><mat-card-title>Summary</mat-card-title></mat-card-header>
              <mat-card-content>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Summary</mat-label>
                  <input matInput [(ngModel)]="summary" maxlength="500" />
                </mat-form-field>
                <div class="color-row" *ngIf="iss.type === 3">
                  <label>Epic color</label>
                  <input type="color" [(ngModel)]="color" />
                </div>
              </mat-card-content>
            </mat-card>

            <mat-card class="panel">
              <mat-card-header><mat-card-title>Workflow transitions</mat-card-title></mat-card-header>
              <mat-card-content>
                @for (t of availableTransitions(); track t.transitionId) {
                  @if (t.blockers.length) {
                    <div class="blocker-list">
                      @for (b of t.blockers; track b) {
                        <div class="blocker"><mat-icon>block</mat-icon> {{ b }}</div>
                      }
                    </div>
                  }
                }
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Comment (required for some transitions)</mat-label>
                  <textarea matInput rows="2" [(ngModel)]="transitionComment"></textarea>
                </mat-form-field>
                <div class="transition-actions">
                  @for (t of availableTransitions(); track t.transitionId) {
                    <button mat-stroked-button type="button"
                            [disabled]="!!t.blockers.length"
                            [color]="transitionNeedsComment(t) && !transitionComment.trim() ? 'warn' : undefined"
                            [matTooltip]="t.blockers.join(' ') || (transitionNeedsComment(t) ? 'Comment required' : t.toStatusName)"
                            (click)="runTransition(t)">
                      {{ t.name }} → {{ t.toStatusName }}
                    </button>
                  } @empty {
                    <p class="muted">No workflow transitions available.</p>
                  }
                </div>
              </mat-card-content>
            </mat-card>

            <mat-card class="panel" *ngIf="iss.dueDate || iss.isOverdue || iss.delayInDays">
              <mat-card-header><mat-card-title>Deadline &amp; Risk</mat-card-title></mat-card-header>
              <mat-card-content>
                <div class="deadline-panel" [class.overdue]="iss.isOverdue">
                  <div class="row"><span>Deadline</span><strong>{{ iss.dueDate || '—' }}</strong></div>
                  <div class="row"><span>Time remaining</span><strong>{{ iss.remainingLabel || '—' }}</strong></div>
                  <div class="row"><span>Delay</span><strong>{{ iss.delayLabel || 'On time' }}</strong></div>
                  <div class="row"><span>Progress</span><strong>{{ iss.progressPercent ?? 0 }}%</strong></div>
                  <div class="row"><span>Risk level</span><strong>{{ iss.riskLevel || 'Low' }}</strong></div>
                  @if (iss.completedOnTime != null) {
                    <div class="row"><span>Completion</span><strong>{{ iss.completedOnTime ? 'Completed On Time' : 'Completed Late' }}</strong></div>
                  }
                  <span class="overdue-pill" *ngIf="iss.isOverdue">Overdue</span>
                  @if (iss.deadlineBadge) {
                    <span class="badge deadline-badge" [ngClass]="deadlineBadgeClass(iss.deadlineBadge, iss.isOverdue, iss.completedOnTime)">{{ iss.deadlineBadge }}</span>
                  } @else if (iss.remainingLabel) {
                    <span class="badge deadline-badge" [ngClass]="deadlineBadgeClass(undefined, iss.isOverdue, iss.completedOnTime)">{{ deadlineDisplayLabel(iss) }}</span>
                  }
                </div>
              </mat-card-content>
            </mat-card>
          </div>

          <div class="side-col">
            <mat-card class="panel">
              <mat-card-header><mat-card-title>Fields</mat-card-title></mat-card-header>
              <mat-card-content>
                <mat-form-field appearance="outline" class="full" *ngIf="!availableTransitions().length">
                  <mat-label>Status</mat-label>
                  <mat-select [(ngModel)]="status" (ngModelChange)="transition($event)">
                    <mat-option *ngFor="let s of statusOptions" [value]="s.value">{{ s.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <p class="muted" *ngIf="availableTransitions().length">Status is controlled by workflow transitions.</p>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Type</mat-label>
                  <mat-select [(ngModel)]="type">
                    <mat-option *ngFor="let t of typeOptions" [value]="t.value">{{ t.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Priority</mat-label>
                  <mat-select [(ngModel)]="priority">
                    <mat-option *ngFor="let p of priorityOptions" [value]="p.value">{{ p.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full" *ngIf="type === 1">
                  <mat-label>Severity</mat-label>
                  <mat-select [(ngModel)]="severity">
                    <mat-option value="">— None —</mat-option>
                    <mat-option *ngFor="let s of severityOptions" [value]="s.value">{{ s.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Risk Level</mat-label>
                  <mat-select [(ngModel)]="riskLevel">
                    <mat-option *ngFor="let r of riskLevelOptions" [value]="r.value">{{ r.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Story points</mat-label>
                  <input matInput type="number" [(ngModel)]="storyPoints" min="0" step="0.5" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Original estimate (hours)</mat-label>
                  <input matInput type="number" [(ngModel)]="originalEstimateHours" min="0" step="0.25" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Remaining estimate (hours)</mat-label>
                  <input matInput type="number" [(ngModel)]="remainingEstimateHours" min="0" step="0.25" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Actual Hours</mat-label>
                  <input matInput type="number" [(ngModel)]="actualHours" min="0" step="0.25" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Assignee</mat-label>
                  <mat-select [(ngModel)]="assigneeId" (ngModelChange)="assign()">
                    <mat-option value="">Unassigned</mat-option>
                    <mat-option *ngFor="let e of employees()" [value]="e.id">{{ e.name }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full" *ngIf="iss.type !== 3">
                  <mat-label>Epic</mat-label>
                  <mat-select [(ngModel)]="epicId" (ngModelChange)="linkEpic()">
                    <mat-option value="">None</mat-option>
                    <mat-option *ngFor="let e of epicOptions()" [value]="e.id">{{ e.key }} — {{ e.summary }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Start date</mat-label>
                  <input matInput type="date" [(ngModel)]="startDate" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Deadline *</mat-label>
                  <input matInput type="date" [(ngModel)]="dueDate" required />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Fix versions</mat-label>
                  <mat-select [(ngModel)]="selectedFixVersionIds" multiple (ngModelChange)="saveVersions()">
                    <mat-option *ngFor="let v of projectVersions()" [value]="v.id">{{ v.name }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Affects versions</mat-label>
                  <mat-select [(ngModel)]="selectedAffectsVersionIds" multiple (ngModelChange)="saveVersions()">
                    <mat-option *ngFor="let v of projectVersions()" [value]="v.id">{{ v.name }}</mat-option>
                  </mat-select>
                </mat-form-field>
              </mat-card-content>
            </mat-card>

            @if (customFieldValues().length) {
              <mat-card class="panel">
                <mat-card-header><mat-card-title>Custom fields</mat-card-title></mat-card-header>
                <mat-card-content>
                  @for (field of customFieldValues(); track field.fieldDefinitionId) {
                    <mat-form-field appearance="outline" class="full">
                      <mat-label>{{ field.fieldName }}</mat-label>
                      <input matInput [type]="customFieldInputType(field.fieldType)"
                        [(ngModel)]="customFieldDraft[field.fieldDefinitionId]" />
                    </mat-form-field>
                  }
                  <button mat-stroked-button type="button" [disabled]="savingCustomFields()" (click)="saveCustomFields()">
                    {{ savingCustomFields() ? 'Saving…' : 'Save custom fields' }}
                  </button>
                </mat-card-content>
              </mat-card>
            }

            <mat-card class="panel">
              <mat-card-header><mat-card-title>Labels</mat-card-title></mat-card-header>
              <mat-card-content>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Apply labels</mat-label>
                  <mat-select [(ngModel)]="selectedLabelIds" multiple>
                    <mat-option *ngFor="let l of labels()" [value]="l.id">{{ l.name }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <div class="inline-create">
                  <mat-form-field appearance="outline" class="grow">
                    <mat-label>New label</mat-label>
                    <input matInput [(ngModel)]="newLabelName" />
                  </mat-form-field>
                  <button mat-icon-button color="primary" (click)="createLabel()"><mat-icon>add</mat-icon></button>
                </div>
              </mat-card-content>
            </mat-card>

            <mat-card class="panel">
              <mat-card-header><mat-card-title>Components</mat-card-title></mat-card-header>
              <mat-card-content>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Apply components</mat-label>
                  <mat-select [(ngModel)]="selectedComponentIds" multiple>
                    <mat-option *ngFor="let c of components()" [value]="c.id">{{ c.name }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <div class="inline-create">
                  <mat-form-field appearance="outline" class="grow">
                    <mat-label>New component</mat-label>
                    <input matInput [(ngModel)]="newComponentName" />
                  </mat-form-field>
                  <button mat-icon-button color="primary" (click)="createComponent()"><mat-icon>add</mat-icon></button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <!-- Description -->
      <mat-tab label="Description">
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="preview-toggle">
                <button mat-stroked-button type="button" (click)="descriptionPreview = !descriptionPreview">
                  <mat-icon>{{ descriptionPreview ? 'edit' : 'visibility' }}</mat-icon>
                  {{ descriptionPreview ? 'Edit' : 'Preview' }}
                </button>
              </div>
              @if (descriptionPreview) {
                <div class="md-preview" [innerHTML]="description | markdown"></div>
                @if (acceptanceCriteria) {
                  <h4>Acceptance Criteria</h4>
                  <div class="md-preview" [innerHTML]="acceptanceCriteria | markdown"></div>
                }
                @if (definitionOfDone) {
                  <h4>Definition of Done</h4>
                  <div class="md-preview" [innerHTML]="definitionOfDone | markdown"></div>
                }
              } @else {
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Description</mat-label>
                  <textarea matInput rows="8" [(ngModel)]="description"></textarea>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Acceptance Criteria</mat-label>
                  <textarea matInput rows="4" [(ngModel)]="acceptanceCriteria"></textarea>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Definition of Done</mat-label>
                  <textarea matInput rows="3" [(ngModel)]="definitionOfDone"></textarea>
                </mat-form-field>
              }
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Checklist -->
      <mat-tab>
        <ng-template mat-tab-label>Checklist <span class="tab-count">{{ checklist().length }}</span></ng-template>
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="checklist-row" *ngFor="let item of checklist()">
                <mat-checkbox [checked]="item.isDone" (change)="toggleChecklistItem(item)">
                  <span [class.done]="item.isDone">{{ item.text }}</span>
                </mat-checkbox>
                <button mat-icon-button class="tiny" (click)="deleteChecklistItem(item)"><mat-icon>delete</mat-icon></button>
              </div>
              <div class="inline-create">
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>Add checklist item</mat-label>
                  <input matInput [(ngModel)]="newChecklistText" (keyup.enter)="addChecklistItem()" />
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!newChecklistText.trim()" (click)="addChecklistItem()">Add</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Subtasks -->
      <mat-tab>
        <ng-template mat-tab-label>Subtasks <span class="tab-count">{{ iss.subtasks.length || 0 }}</span></ng-template>
        <div class="tab-body">
          <mat-card class="panel" *ngIf="iss.type !== 5">
            <mat-card-content>
              <div class="child-row" *ngFor="let s of iss.subtasks">
                <button mat-button color="primary" (click)="openIssue(s.id)">{{ s.key }}</button>
                <span class="grow">{{ s.summary }}</span>
                <span class="chip status">{{ s.statusLabel }}</span>
              </div>
              <div class="inline-create">
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>New sub-task</mat-label>
                  <input matInput [(ngModel)]="newSubtaskSummary" />
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!newSubtaskSummary.trim()" (click)="addSubtask()">Add</button>
              </div>
            </mat-card-content>
          </mat-card>
          <mat-card class="panel" *ngIf="iss.type === 3">
            <mat-card-header><mat-card-title>Epic children ({{ epicChildren().length }})</mat-card-title></mat-card-header>
            <mat-card-content>
              <div class="child-row" *ngFor="let c of epicChildren()">
                <button mat-button color="primary" (click)="openIssue(c.id)">{{ c.key }}</button>
                <span class="grow">{{ c.summary }}</span>
                <span class="chip status">{{ c.statusLabel }}</span>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Links -->
      <mat-tab label="Links">
        <div class="tab-body">
          @if (dependencyGraph(); as graph) {
            <mat-card class="panel deps-panel">
              <mat-card-header>
                <mat-card-title>Dependency graph</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="dep-graph-wrap">
                  <svg class="dep-graph" [attr.viewBox]="'0 0 ' + graph.width + ' ' + graph.height"
                    [attr.width]="graph.width" [attr.height]="graph.height">
                    <defs>
                      <marker id="depArrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6 Z" fill="#64748b"></path>
                      </marker>
                    </defs>
                    @for (e of graph.edges; track $index) {
                      <line [attr.x1]="e.x1" [attr.y1]="e.y1" [attr.x2]="e.x2" [attr.y2]="e.y2"
                        stroke="#94a3b8" stroke-width="1.5" marker-end="url(#depArrow)"></line>
                      <text [attr.x]="(e.x1 + e.x2) / 2" [attr.y]="(e.y1 + e.y2) / 2 - 6"
                        text-anchor="middle" class="dep-edge-label">{{ e.label }}</text>
                    }
                    @for (n of graph.nodes; track n.id) {
                      <g class="dep-node" [class.current]="n.role === 'current'"
                        (click)="n.role !== 'current' && openIssue(n.id)" style="cursor: pointer">
                        <rect [attr.x]="n.x - 36" [attr.y]="n.y - 14" width="72" height="28" rx="6"></rect>
                        <text [attr.x]="n.x" [attr.y]="n.y + 4" text-anchor="middle">{{ n.key }}</text>
                      </g>
                    }
                  </svg>
                  <p class="muted dep-legend">Upstream (left) → this issue → Downstream (right). Click a node to open.</p>
                </div>
              </mat-card-content>
            </mat-card>
          }

          @if (dependencyRows(); as depGroups) {
            @if (depGroups.length) {
              <mat-card class="panel deps-panel">
                <mat-card-header>
                  <mat-card-title>Dependencies</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  @for (group of depGroups; track group.title) {
                    <div class="deps-group">
                      <h4>{{ group.title }}</h4>
                      @for (l of group.items; track l.id) {
                        <div class="child-row">
                          <span class="chip type">{{ l.linkTypeLabel }}</span>
                          <button mat-button color="primary" type="button"
                            (click)="openIssue(l.isOutbound ? l.targetIssueId : l.sourceIssueId)">
                            {{ l.isOutbound ? l.targetIssueKey : l.sourceIssueKey }}
                          </button>
                          <span class="grow" *ngIf="l.isOutbound">{{ l.targetIssueSummary }}</span>
                          <span class="chip status" *ngIf="l.statusLabel">{{ l.statusLabel }}</span>
                          <span class="muted" *ngIf="l.reason">{{ l.reason }}</span>
                          <span class="muted" *ngIf="l.createdAt">{{ l.createdAt | date:'short' }}</span>
                        </div>
                      }
                    </div>
                  }
                </mat-card-content>
              </mat-card>
            }
          }

          <mat-card class="panel">
            <mat-card-header>
              <mat-card-title>All links</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="child-row" *ngFor="let l of iss.links">
                <span class="chip type">{{ l.linkTypeLabel }}</span>
                <button mat-button color="primary" (click)="openIssue(l.isOutbound ? l.targetIssueId : l.sourceIssueId)">
                  {{ l.isOutbound ? l.targetIssueKey : l.sourceIssueKey }}
                </button>
                <span class="grow" *ngIf="l.isOutbound">{{ l.targetIssueSummary }}</span>
                <span class="chip status" *ngIf="l.statusLabel">{{ l.statusLabel }}</span>
                <span class="muted" *ngIf="l.reason" [matTooltip]="l.reason || ''">{{ l.reason }}</span>
                <span class="muted" *ngIf="l.createdAt">{{ l.createdAt | date:'short' }}</span>
                <mat-form-field appearance="outline" class="link-status-field" *ngIf="l.isOutbound">
                  <mat-label>Status</mat-label>
                  <mat-select [ngModel]="l.status ?? 0" (ngModelChange)="updateLinkStatus(l.id, $event)">
                    <mat-option *ngFor="let s of linkStatusOptions" [value]="s.value">{{ s.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <button mat-icon-button class="tiny" (click)="removeLink(l.id)"><mat-icon>link_off</mat-icon></button>
              </div>
              <div class="inline-create wrap">
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>Link to issue</mat-label>
                  <mat-select [(ngModel)]="linkTargetId">
                    <mat-option *ngFor="let opt of linkTargetOptions()" [value]="opt.id">{{ opt.key }} — {{ opt.summary }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Type</mat-label>
                  <mat-select [(ngModel)]="linkType">
                    <mat-option *ngFor="let t of linkTypeOptions" [value]="t.value">{{ t.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Status</mat-label>
                  <mat-select [(ngModel)]="linkStatus">
                    <mat-option *ngFor="let s of linkStatusOptions" [value]="s.value">{{ s.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>Reason (optional)</mat-label>
                  <input matInput [(ngModel)]="linkReason" maxlength="500" />
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!linkTargetId" (click)="addLink()">Link</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Attachments -->
      <mat-tab>
        <ng-template mat-tab-label>Attachments <span class="tab-count">{{ iss.attachments.length || 0 }}</span></ng-template>
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="child-row" *ngFor="let a of iss.attachments">
                <mat-icon>attach_file</mat-icon>
                <a *ngIf="a.url; else plainName" [href]="a.url" target="_blank" rel="noopener">{{ a.originalFileName }}</a>
                <ng-template #plainName><span class="grow">{{ a.originalFileName }}</span></ng-template>
                <span class="muted">{{ a.uploadedByName }} · {{ a.createdAt | date:'short' }}</span>
                <button mat-icon-button class="tiny" (click)="removeAttachment(a.id)"><mat-icon>delete</mat-icon></button>
              </div>
              <div class="inline-create wrap">
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>File name</mat-label>
                  <input matInput [(ngModel)]="attachmentName" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>URL (optional)</mat-label>
                  <input matInput [(ngModel)]="attachmentUrl" />
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!attachmentName.trim()" (click)="addAttachment()">Add</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Comments -->
      <mat-tab>
        <ng-template mat-tab-label>Comments <span class="tab-count">{{ commentCount(iss.comments) }}</span></ng-template>
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <ng-container *ngFor="let c of iss.comments">
                <div class="comment">
                  <div class="comment-head">
                    <strong>{{ c.authorName }}</strong>
                    <span class="muted">{{ c.createdAt | date:'medium' }}</span>
                    <button mat-button class="tiny-text" type="button" (click)="startReply(c.id)">Reply</button>
                    <button mat-icon-button class="tiny" (click)="deleteComment(c.id)"><mat-icon>delete</mat-icon></button>
                  </div>
                  <p>{{ c.body }}</p>
                  <div class="reactions">
                    <button mat-stroked-button type="button" class="react-btn"
                      *ngFor="let r of c.reactions || []"
                      [class.active]="r.reactedByMe"
                      (click)="toggleReaction(c.id, r.emoji)">
                      {{ r.emoji }} {{ r.count }}
                    </button>
                    <button mat-icon-button type="button" class="tiny" *ngFor="let e of reactionEmojis"
                      [matTooltip]="'React ' + e" (click)="toggleReaction(c.id, e)">
                      <span class="emoji-only">{{ e }}</span>
                    </button>
                  </div>

                  <div class="reply-box" *ngIf="replyToCommentId === c.id">
                    <mat-form-field appearance="outline" class="full">
                      <mat-label>Reply</mat-label>
                      <textarea matInput rows="2" [(ngModel)]="replyBody"></textarea>
                    </mat-form-field>
                    <button mat-stroked-button color="primary" [disabled]="!replyBody.trim()" (click)="submitReply()">Post reply</button>
                    <button mat-button type="button" (click)="cancelReply()">Cancel</button>
                  </div>

                  <div class="replies" *ngIf="c.replies?.length">
                    <div class="comment reply" *ngFor="let r of c.replies">
                      <div class="comment-head">
                        <strong>{{ r.authorName }}</strong>
                        <span class="muted">{{ r.createdAt | date:'medium' }}</span>
                        <button mat-icon-button class="tiny" (click)="deleteComment(r.id)"><mat-icon>delete</mat-icon></button>
                      </div>
                      <p>{{ r.body }}</p>
                      <div class="reactions">
                        <button mat-stroked-button type="button" class="react-btn"
                          *ngFor="let rx of r.reactions || []"
                          [class.active]="rx.reactedByMe"
                          (click)="toggleReaction(r.id, rx.emoji)">
                          {{ rx.emoji }} {{ rx.count }}
                        </button>
                        <button mat-icon-button type="button" class="tiny" *ngFor="let e of reactionEmojis"
                          [matTooltip]="'React ' + e" (click)="toggleReaction(r.id, e)">
                          <span class="emoji-only">{{ e }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <mat-divider></mat-divider>
                </div>
              </ng-container>
              <div class="add-comment">
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Add a comment</mat-label>
                  <textarea matInput rows="3" [(ngModel)]="newComment"></textarea>
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!newComment.trim()" (click)="addComment()">Comment</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Activity -->
      <mat-tab label="Activity">
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="timeline" *ngFor="let a of activity()">
                <strong>{{ a.activityTypeLabel }}</strong>
                <span class="muted">{{ a.occurredAt | date:'medium' }} · {{ a.actorName || 'System' }}</span>
                <p>{{ a.message }}</p>
              </div>
              <p class="muted" *ngIf="!activity().length">No activity yet.</p>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- History -->
      <mat-tab label="History">
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="timeline" *ngFor="let h of history()">
                <strong>{{ h.fieldName }}</strong>
                <span class="muted">{{ h.changedAt | date:'medium' }} · {{ h.changedByName || 'System' }}</span>
                <p><span class="old">{{ h.oldValue || '—' }}</span> → <span class="new">{{ h.newValue || '—' }}</span></p>
              </div>
              <p class="muted" *ngIf="!history().length">No history yet.</p>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Time Tracking -->
      <mat-tab label="Time Tracking">
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-header>
              <mat-card-title>Logged: {{ issueLoggedHours() }}h</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="time-toolbar">
                <button mat-flat-button color="primary" type="button" (click)="startIssueTimer()">
                  <mat-icon>play_arrow</mat-icon> Start timer
                </button>
                <mat-spinner *ngIf="loadingTimeEntries()" diameter="24"></mat-spinner>
              </div>

              <h4>Work logs</h4>
              @if (loadingTimeEntries()) {
                <mat-spinner diameter="24"></mat-spinner>
              } @else if (issueTimeEntries().length) {
                <table mat-table [dataSource]="issueTimeEntries()" class="worklog-table full">
                  <ng-container matColumnDef="date">
                    <th mat-header-cell *matHeaderCellDef>Date</th>
                    <td mat-cell *matCellDef="let e">{{ e.date }}</td>
                  </ng-container>
                  <ng-container matColumnDef="hours">
                    <th mat-header-cell *matHeaderCellDef>Hours</th>
                    <td mat-cell *matCellDef="let e">{{ e.hours }}h</td>
                  </ng-container>
                  <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef>Status</th>
                    <td mat-cell *matCellDef="let e">
                      <span class="chip" [class.running]="e.isRunning">{{ e.isRunning ? 'Running' : timeStatusLabel(e.status) }}</span>
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="description">
                    <th mat-header-cell *matHeaderCellDef>Description</th>
                    <td mat-cell *matCellDef="let e">{{ e.description || '—' }}</td>
                  </ng-container>
                  <tr mat-header-row *matHeaderRowDef="worklogColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: worklogColumns"></tr>
                </table>
                @for (e of issueTimeEntries(); track e.id) {
                  @if (e.isRunning || (e.startedAt && !e.endedAt)) {
                    <div class="time-actions">
                      @if (e.isRunning) {
                        <button mat-stroked-button type="button" (click)="pauseIssueTimer(e)">Pause</button>
                        <button mat-stroked-button type="button" (click)="stopIssueTimer(e)">Stop</button>
                      } @else {
                        <button mat-stroked-button type="button" (click)="resumeIssueTimer(e)">Resume</button>
                      }
                    </div>
                  }
                }
              } @else {
                <p class="muted">No time logged for this issue yet.</p>
              }

              <h4>Manual work log</h4>
              <div class="inline-create wrap">
                <mat-form-field appearance="outline">
                  <mat-label>Date</mat-label>
                  <input matInput type="date" [(ngModel)]="manualLogDate" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Hours</mat-label>
                  <input matInput type="number" [(ngModel)]="manualLogHours" min="0.25" step="0.25" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>Description</mat-label>
                  <input matInput [(ngModel)]="manualLogDescription" />
                </mat-form-field>
                <button mat-stroked-button color="primary" [disabled]="!manualLogHours" (click)="addManualTimeLog()">Log time</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <!-- Watchers -->
      <mat-tab>
        <ng-template mat-tab-label>Watchers <span class="tab-count">{{ watchers().length }}</span></ng-template>
        <div class="tab-body">
          <mat-card class="panel">
            <mat-card-content>
              <div class="child-row" *ngFor="let w of watchers()">
                <mat-icon>visibility</mat-icon>
                <span class="grow">{{ w.userName }}</span>
                <span class="muted">{{ w.watchedAt | date:'short' }}</span>
              </div>
              <p class="muted" *ngIf="!watchers().length">No watchers yet.</p>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
    </mat-tab-group>
  </ng-container>
</div>
